/**
 * PlantCard — polished, shared plant/company card for /plants and /directory.
 *
 * Presentation-only. It renders whatever manufacturer document is passed in
 * (as returned by GET /api/manufacturers). Zero API calls, zero writes,
 * zero backend touch. Uses only fields that already exist on the record.
 *
 * Hierarchy (per Corridor Digital spec):
 *   1. Plant / company name (largest, strongest)
 *   2. Plant sequence (P01…) + city, state — directly beneath the name
 *   3. Government-listed IMMEX / Not Corridor Verified badge — prominent
 *   4. IMMEX reference + exact address — compact metadata
 *   5. Park — only when supported
 *   6. "N plant locations" subtle label — only for repeated legal entities
 *   7. Capabilities — real values or a neutral "Capabilities not yet submitted"
 *   8. "View Plant Profile" action
 *
 * Fallback chain for the name (user-approved):
 *   display_name → company_name → legal_entity → "Government-listed IMMEX plant"
 *
 * Truth labels the card exposes:
 *   Government-listed IMMEX (slug begins with "immex-")
 *   Not Corridor Verified (default when corridor_verified is not true)
 *   Corridor Verified (green)
 *   Company Claimed (blue) — or Unclaimed
 *   Legacy Verified — review (orange, only when verified=true but corridor_verified≠true)
 *   Demo / Test (yellow, when is_demo)
 *   Review Candidate (orange, for the six flagged duplicate slugs)
 */
import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Eye, Building2 } from "lucide-react";
import {
  GovListedBadge,
  PendingVerificationBadge,
  EvidenceBackedBadge,
  CorridorVerifiedBadge,
  LegacyVerifiedBadge,
  ClaimedBadge,
  UnclaimedBadge,
  DemoBadge,
  ReviewCandidateBadge,
  DUPLICATE_CANDIDATE_SLUGS,
} from "../pages/adminUiHelpers";

/**
 * Compute the display bundle for one manufacturer document. Pure function —
 * no defaults are invented. If a field is absent it is returned as "" or
 * null; the render layer chooses whether to show a dash or hide the line.
 *
 * Name resolution (strict, per owner spec):
 *   1. display_name
 *   2. company_name
 *   3. legal_name
 *   4. legal_entity
 *   5. Fallback text — "Government-listed IMMEX plant" for slugs that
 *      begin with "immex-", "Unnamed directory record" for legacy rows
 *      that pre-date the audited IMMEX release. The IMMEX number, city,
 *      address, and slug are NEVER used as name replacements.
 */
export function computePlantDisplay(m, lang = "en") {
  const isImmex = (m.slug || "").startsWith("immex-");
  const isDup = DUPLICATE_CANDIDATE_SLUGS.has((m.slug || "").toLowerCase());
  const isDemo = m.is_demo === true;

  // ── Name — strict fallback chain
  const nameCandidates = [
    m.display_name,
    m.company_name,
    m.legal_name,      // user-specified 3rd candidate; API currently doesn't
                       // populate this, kept in the chain for future imports.
    m.legal_entity,
  ];
  let displayName = "";
  for (const c of nameCandidates) {
    if (c && String(c).trim()) {
      displayName = String(c).trim();
      break;
    }
  }
  if (!displayName) {
    // Genuinely nameless. Use the IMMEX-specific fallback ONLY when the
    // slug identifies the row as coming from the government IMMEX registry;
    // never claim IMMEX provenance for a legacy pre-audit row that simply
    // has no name field on it.
    displayName = isImmex
      ? (lang === "es" ? "Planta IMMEX listada por gobierno" : "Government-listed IMMEX plant")
      : (lang === "es" ? "Registro sin nombre" : "Unnamed directory record");
  }

  const plantSequence = m.plant_sequence_label || null;         // e.g. "P01"
  const isAdditional = m.is_additional_plant_location === true;
  const plantCount = m.plant_count_for_legal_entity;            // integer or undefined
  const city = m.city || "";
  const state = m.state || "";
  const cityState = [city, state].filter(Boolean).join(", ");

  // Address — prefer the backfilled plant_address; fall back to street_address.
  const address = m.plant_address || m.street_address || m.address || "";
  const postal = m.postal_code || m.zip_code || "";
  const colonia = m.colonia || "";

  const immexRef =
    m.immex_program_reference ||
    m.immex_number || "";
  const immexYear = m.immex_program_year || null;

  // Park — accept the backfilled name AND the raw CSV column name in case an
  // older import path used the untransformed key.
  const park =
    (m.assigned_park && String(m.assigned_park).trim()) ||
    (m.assigned_park_or_zone && String(m.assigned_park_or_zone).trim()) ||
    null;

  const caps = Array.isArray(m.capabilities) ? m.capabilities.filter(Boolean) : [];
  const inds = Array.isArray(m.industries) ? m.industries.filter(Boolean) : [];
  const capabilities = caps.length ? caps : inds; // never inventing anything — only real values

  return {
    isImmex,
    isDup,
    isDemo,
    displayName,
    plantSequence,
    isAdditional,
    plantCount,
    city,
    state,
    cityState,
    address,
    postal,
    colonia,
    immexRef,
    immexYear,
    park,
    capabilities,
    evidenceBacked:
      !!m.capability_evidence_version ||
      (Array.isArray(m.capability_evidence) && m.capability_evidence.length > 0),
    profileViews: Number(m.profile_views || 0),
    corridorVerified: m.corridor_verified === true,
    legacyVerified: m.verified === true && m.corridor_verified !== true,
    claimed: m.claimed === true,
    slug: m.slug,
    profileId: m.profile_id,
  };
}

/**
 * Compact badge row — Government-listed, Claimed/Unclaimed, Corridor Verified,
 * Demo/Test, Review Candidate.
 */
function TruthBadges({ d, lang = "en" }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`plant-badges-${d.slug}`}>
      {d.isImmex ? <GovListedBadge lang={lang} testid={`plant-badge-gov-${d.slug}`} /> : null}
      {d.corridorVerified ? <CorridorVerifiedBadge testid={`plant-badge-corridor-${d.slug}`} /> : null}
      {!d.corridorVerified ? <PendingVerificationBadge lang={lang} testid={`plant-badge-pending-${d.slug}`} /> : null}
      {d.evidenceBacked ? <EvidenceBackedBadge lang={lang} testid={`plant-badge-evidence-${d.slug}`} /> : null}
      {d.legacyVerified ? <LegacyVerifiedBadge testid={`plant-badge-legacy-${d.slug}`} /> : null}
      {d.claimed ? <ClaimedBadge testid={`plant-badge-claimed-${d.slug}`} /> : (d.isImmex ? <UnclaimedBadge testid={`plant-badge-unclaimed-${d.slug}`} /> : null)}
      {d.isDemo ? <DemoBadge testid={`plant-badge-demo-${d.slug}`} /> : null}
      {d.isDup ? <ReviewCandidateBadge testid={`plant-badge-review-${d.slug}`} /> : null}
    </div>
  );
}

/**
 * The card. Always links to `/m/{slug}` so every plant opens its unique
 * public profile page. The link wraps the whole card for a big tap target.
 */
export function PlantCard({ m, lang = "en" }) {
  const d = computePlantDisplay(m, lang);

  const capabilitiesLabel = d.capabilities.length
    ? null
    : (lang === "es"
        ? "Capacidades aún no enviadas"
        : "Capabilities not yet submitted");

  const viewLabel = lang === "es" ? "Ver perfil de la planta" : "View Plant Profile";

  return (
    <Link
      to={`/m/${d.slug}`}
      className="cd-card cd-card-interactive block"
      style={{
        padding: "20px 20px 18px",
        minHeight: 240,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        background: d.isDup ? "#fffaf5" : "white",
      }}
      data-testid={`plant-card-${d.slug}`}
    >
      {/* 1 · Name (strongest) */}
      <h3
        className="font-heading font-semibold"
        style={{
          fontSize: "clamp(18px, 1.4vw, 22px)",
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
          color: "var(--ink)",
        }}
        data-testid={`plant-name-${d.slug}`}
      >
        {d.displayName}
      </h3>

      {/* 2 · Plant sequence + city / state — directly beneath the name */}
      <div
        className="mt-1 font-mono text-xs flex flex-wrap items-center"
        style={{ color: "var(--ink-2)", gap: "6px 10px" }}
        data-testid={`plant-subline-${d.slug}`}
      >
        {d.plantSequence ? (
          <span
            style={{
              padding: "1px 6px",
              background: "#f1f5f9",
              color: "var(--ink)",
              borderRadius: 2,
              letterSpacing: "0.03em",
            }}
            data-testid={`plant-seq-${d.slug}`}
          >
            {d.plantSequence}
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1">
          <MapPin size={11} strokeWidth={1.6} />
          {d.cityState || "—"}
        </span>
        {d.isAdditional && d.plantCount && d.plantCount > 1 ? (
          <span
            style={{ color: "var(--ink-2)" }}
            title={lang === "es"
              ? "Ubicación adicional bajo el mismo programa IMMEX"
              : "Additional plant location — same IMMEX holder"}
            data-testid={`plant-additional-${d.slug}`}
          >
            · {lang === "es" ? "Ubicación adicional" : "Additional plant location"}
          </span>
        ) : null}
      </div>

      {/* 3 · Truth badges — prominent */}
      <div className="mt-3">
        <TruthBadges d={d} lang={lang} />
      </div>

      {/* 4 + 5 · IMMEX ref + address, and park when supported */}
      <div
        className="mt-3 font-mono text-xs"
        style={{ color: "var(--ink-2)", lineHeight: 1.55 }}
        data-testid={`plant-meta-${d.slug}`}
      >
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="overline" style={{ color: "var(--ink-2)" }}>IMMEX</span>
          <span style={{ color: "var(--ink)" }}>
            {d.immexRef || "—"}
            {d.immexYear ? <span style={{ color: "var(--ink-2)" }}> · {d.immexYear}</span> : null}
          </span>
        </div>
        <div className="mt-1">
          <span className="overline" style={{ color: "var(--ink-2)" }}>
            {lang === "es" ? "DIR" : "ADDR"}
          </span>{" "}
          <span style={{ color: "var(--ink)" }}>{d.address || (lang === "es" ? "No enviada" : "Not submitted")}</span>
          {d.postal ? <span style={{ color: "var(--ink-2)" }}> · {d.postal}</span> : null}
        </div>
        {d.park ? (
          <div className="mt-1" data-testid={`plant-park-${d.slug}`}>
            <span className="overline" style={{ color: "var(--ink-2)" }}>PARK</span>{" "}
            <span style={{ color: "var(--ink)" }}>{d.park}</span>
          </div>
        ) : null}
      </div>

      {/* 6 · N plant locations — subtle */}
      {d.plantCount && d.plantCount > 1 ? (
        <div
          className="mt-3 font-mono text-xs inline-flex items-center gap-1.5"
          style={{ color: "var(--ink-2)" }}
          data-testid={`plant-count-${d.slug}`}
        >
          <Building2 size={11} strokeWidth={1.6} />
          {d.plantCount} {lang === "es" ? "ubicaciones de planta" : "plant locations"}
        </div>
      ) : null}

      {/* 7 · Capabilities — real values or a NEUTRAL empty state (no invented values) */}
      <div className="mt-4" data-testid={`plant-caps-${d.slug}`}>
        {d.capabilities.length ? (
          <div className="flex flex-wrap gap-1.5">
            {d.capabilities.slice(0, 4).map((c, i) => (
              <span
                key={i}
                className="cd-chip"
                style={{ fontSize: 10 }}
                data-testid={`plant-cap-${d.slug}-${i}`}
              >
                {c}
              </span>
            ))}
            {d.capabilities.length > 4 ? (
              <span
                className="font-mono text-xs"
                style={{ color: "var(--ink-2)", alignSelf: "center" }}
              >
                +{d.capabilities.length - 4}
              </span>
            ) : null}
          </div>
        ) : (
          <div
            className="font-mono text-xs"
            style={{
              color: "var(--ink-2)",
              padding: "6px 10px",
              background: "var(--bg)",
              border: "1px dashed var(--line)",
              display: "inline-block",
              borderRadius: 2,
            }}
            data-testid={`plant-caps-empty-${d.slug}`}
          >
            {capabilitiesLabel}
          </div>
        )}
      </div>

      {/* Filler pushes the CTA row to the bottom */}
      <div style={{ flex: 1, minHeight: 8 }} />

      {/* Views (only when > 0 — never invent) */}
      {d.profileViews > 0 ? (
        <div
          className="mt-3 font-mono text-xs inline-flex items-center gap-1.5"
          style={{ color: "var(--ink-2)" }}
          data-testid={`plant-views-${d.slug}`}
        >
          <Eye size={11} strokeWidth={1.6} /> {d.profileViews} {lang === "es" ? "vistas" : "views"}
        </div>
      ) : null}

      {/* 8 · CTA */}
      <div
        className="mt-4 pt-3 flex items-center justify-between"
        style={{ borderTop: "1px solid var(--line)" }}
      >
        <span
          className="font-mono text-xs"
          style={{ color: "var(--ink)", letterSpacing: "0.03em" }}
        >
          {viewLabel}
        </span>
        <ArrowRight size={14} strokeWidth={1.6} />
      </div>
    </Link>
  );
}
